import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { checkAdminSession, validateCredential, commonLogin } from "../common/api/loginApi";
import { hasAuthenticatedSession } from "../common/utils/sessionContext";
import { resolvePostLoginNavigation, resolveSessionLandingRoute } from "../common/utils/authSession";
import AlertMessage from "../common/components/AlertMessage";
import Backdrop from "@mui/material/Backdrop";
import * as Sentry from "@sentry/react";
import { FaChevronRight, FaChevronLeft, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaUserNurse, FaUsers, FaUserShield } from "react-icons/fa";
import "../styles/newLogin.css";

// This screen accepts an email only — mobile-number identifiers are not offered
// here even though validateUserCredential.do also accepts them.
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const STEP_EMAIL = "email";
const STEP_ROLE = "role";
const STEP_PASSWORD = "password";

const ROLE_META = {
  CAREGIVER: {
    label: "Caregiver",
    description: "Check in, log vitals and complete tasks",
    Icon: FaUserNurse,
  },
  FAMILYMEMBER: {
    label: "Family Member",
    description: "Follow your loved one's care and updates",
    Icon: FaUsers,
  },
  ADMIN: {
    label: "Administrator",
    description: "Manage communities, staff and residents",
    Icon: FaUserShield,
  },
};

const describeRole = (role) => ROLE_META[role] || { label: role, description: "", Icon: FaUsers };

const NewLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [step, setStep] = useState(STEP_EMAIL);
  const [emailId, setEmailId] = useState("");
  const [password, setPassword] = useState("");
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingSession, setCheckingSession] = useState(true);
  const [alertStatus, setAlertStatusBase] = useState("");

  // Derived rather than held in state: nothing to keep in sync.
  const emailValid = EMAIL_REGEX.test(emailId.trim());
  const submitDisabled = step === STEP_PASSWORD ? !password.length : !emailValid;

  useEffect(() => {
    const prefilledEmail = location.state?.email?.trim() || "";
    if (prefilledEmail) {
      setEmailId(prefilledEmail.toLowerCase());
    }
  }, [location.state?.email]);

  useEffect(() => {
    // 1. FP / CG session lives in localStorage — treat it as the source of
    //    truth first, so those users never wait on a network round-trip.
    if (hasAuthenticatedSession()) {
      navigate(resolveSessionLandingRoute());
      return;
    }

    // 2. No in-app session: the user may still hold an active admin (JSP)
    //    session in the JSESSIONID cookie. session.do reports it and where to
    //    land (mainMenu.jsp vs community.jsp).
    let active = true;

    (async () => {
      const adminSession = await checkAdminSession();

      if (!active) {
        return;
      }

      if (adminSession.authenticated && adminSession.role === "admin" && adminSession.redirectUrl) {
        window.location.assign(adminSession.redirectUrl);
        return;
      }

      // 3. Nobody is signed in → reveal the React login form.
      setCheckingSession(false);
    })();

    return () => {
      active = false;
    };
  }, [navigate]);

  const showError = (message) => {
    setAlertStatusBase({ message, status: "400", origin: "center" });
  };

  /** Step 1 → 2: confirm the email exists and learn which role(s) it holds. */
  const submitEmail = async () => {
    const normalizedEmail = emailId.trim().toLowerCase();
    const res = await validateCredential({ emailId: normalizedEmail });

    if (!res.success) {
      showError(res.message);
      return;
    }

    localStorage.setItem("userEmail", normalizedEmail);
    setRoles(res.roles);
    setStep(STEP_ROLE);
  };

  /** Step 3: the picked role plus the password complete the sign-in. */
  const submitPassword = async () => {
    const normalizedEmail = emailId.trim().toLowerCase();
    const loginRes = await commonLogin({
      identifier: normalizedEmail,
      password,
      role: selectedRole,
    });

    if (!loginRes.success) {
      showError(loginRes.message);
      return;
    }

    // Admin: session already established server-side; navigate to the JSP
    // admin app (the one external hand-off). Caregiver/family continue in-app.
    if (loginRes.role === "admin") {
      window.location.assign(loginRes.redirectUrl);
      return;
    }

    const navigation = await resolvePostLoginNavigation(loginRes, normalizedEmail);

    if (!navigation.success) {
      showError(navigation.message);
      return;
    }

    navigate(navigation.path, navigation.state ? { state: navigation.state } : undefined);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitDisabled || loading) {
      return;
    }

    setLoading(true);

    try {
      if (step === STEP_EMAIL) {
        await submitEmail();
      } else if (step === STEP_PASSWORD) {
        await submitPassword();
      }
    } catch (error) {
      Sentry.captureException(error);
      showError("Unable to sign in. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const chooseRole = (role) => {
    setSelectedRole(role);
    setPassword("");
    setShowPassword(false);
    setStep(STEP_PASSWORD);
  };

  const backToEmail = () => {
    setStep(STEP_EMAIL);
    setRoles([]);
    setSelectedRole("");
    setPassword("");
    setShowPassword(false);
  };

  const backToRoles = () => {
    setStep(STEP_ROLE);
    setSelectedRole("");
    setPassword("");
    setShowPassword(false);
  };

  const closeAlert = () => {
    setAlertStatusBase("");
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password", { state: { email: emailId.trim() } });
  };

  const updateEmail = (e) => {
    setEmailId(e.target.value.replace(/\s/g, "").toLowerCase());
  };

  const headings = {
    [STEP_EMAIL]: { title: "Welcome back", sub: "Enter your email to continue." },
    [STEP_ROLE]: { title: "Choose your role", sub: "This email is registered for more than one role." },
    [STEP_PASSWORD]: { title: "Enter your password", sub: `Signing in as ${describeRole(selectedRole).label}.` },
  };
  const heading = headings[step];

  return (
    <main className="nl-login-page">
      <Backdrop sx={{ color: "#ffba30", zIndex: (theme) => theme.zIndex.drawer + 1000 }} open={loading || checkingSession}>
        <img src={require("../assets/btTree.png")} className="loading-img" alt="" />
      </Backdrop>
      {alertStatus ? <AlertMessage status={alertStatus.status} message={alertStatus.message} origin={alertStatus.origin} closeAlert={closeAlert} /> : null}

      <section className="nl-login-brand-pane">
        <div className="nl-login-glow" aria-hidden="true" />
        <div className="nl-login-brand-inner">
          <h1 className="nl-login-brand-heading">
            BT HomeCare
            <br />
            <span className="nl-login-brand-heading-sub">Digital Care Communication</span>
          </h1>
          <p className="nl-login-brand-tagline">Care that connects, communication that grows.</p>
          <p className="nl-login-brand-tagline">Bringing caregivers, families, and care teams together to deliver better outcomes.</p>
        </div>
      </section>

      <section className="nl-login-form-pane">
        <div className="nl-login-card">
          <div className="nl-login-card-header">
            <img className="nl-login-card-logo" src={require("../assets/Loginpage_logo - Copy.png")} alt="BridgeTree Digital Care Communications" />
          </div>

          <h2 className="nl-login-welcome">{heading.title}</h2>
          <p className="nl-login-welcome-sub">{heading.sub}</p>

          {step !== STEP_EMAIL ? (
            <div className="nl-login-identity">
              <FaEnvelope size={12} aria-hidden="true" />
              <span className="nl-login-identity-email">{emailId.trim()}</span>
              <button type="button" className="nl-login-identity-change" onClick={backToEmail}>
                Change
              </button>
            </div>
          ) : null}

          {step === STEP_ROLE ? (
            <div className="nl-login-role-list" role="group" aria-label="Select a role">
              {roles.map(({ role, firstLogin }) => {
                const { label, description, Icon } = describeRole(role);
                return (
                  <button key={role} type="button" className="nl-login-role-option" onClick={() => chooseRole(role)}>
                    <span className="nl-login-role-icon" aria-hidden="true">
                      <Icon size={18} />
                    </span>
                    <span className="nl-login-role-text">
                      <span className="nl-login-role-label">
                        {label}
                        {firstLogin ? <span className="nl-login-role-badge">First login</span> : null}
                      </span>
                      <span className="nl-login-role-desc">{description}</span>
                    </span>
                    <FaChevronRight size={13} className="nl-login-role-chevron" aria-hidden="true" />
                  </button>
                );
              })}
            </div>
          ) : (
            <form className="nl-login-form" onSubmit={handleSubmit}>
              {step === STEP_EMAIL ? (
                <div className="nl-login-field">
                  <label htmlFor="nl-login-email" className="nl-login-label">
                    Email
                  </label>
                  <div className="nl-login-input-wrap">
                    <FaEnvelope size={14} className="nl-login-input-icon" aria-hidden="true" />
                    <input id="nl-login-email" type="email" name="emailId" value={emailId} onChange={updateEmail} placeholder="Email" autoComplete="email" maxLength={75} required />
                  </div>
                </div>
              ) : (
                <div className="nl-login-field">
                  <label htmlFor="nl-login-password" className="nl-login-label">
                    Password
                  </label>
                  <div className="nl-login-input-wrap">
                    <FaLock size={14} className="nl-login-input-icon" aria-hidden="true" />
                    <input
                      id="nl-login-password"
                      type={showPassword ? "text" : "password"}
                      name="password"
                      className="nl-login-input--with-action"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      maxLength={75}
                      required
                      autoFocus
                    />
                    <button type="button" className="nl-login-toggle-visibility" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? "Hide password" : "Show password"}>
                      {showPassword ? <FaEye /> : <FaEyeSlash />}
                    </button>
                  </div>
                </div>
              )}

              {step === STEP_PASSWORD ? (
                <div className="nl-login-options-row">
                  <button type="button" className="nl-login-forgot" onClick={handleForgotPassword}>
                    Forgot password?
                  </button>
                </div>
              ) : null}

              <button type="submit" className="nl-login-submit" disabled={submitDisabled}>
                {step === STEP_EMAIL ? (
                  <>
                    Continue
                    <FaChevronRight size={16} aria-hidden="true" />
                  </>
                ) : (
                  "Sign in"
                )}
              </button>

              {step === STEP_PASSWORD && roles.length > 1 ? (
                <button type="button" className="nl-login-step-back" onClick={backToRoles}>
                  <FaChevronLeft size={11} aria-hidden="true" />
                  Choose a different role
                </button>
              ) : null}
            </form>
          )}
        </div>
      </section>
    </main>
  );
};

export default NewLogin;
